import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { ConfirmDialog } from '../components/confirm-dialog.js';
import { magToast } from '../components/toaster.js';
import { TopNav } from '../components/top-nav.js';
import {
  type Brand,
  type BrandChannelDTO,
  type BrandChannelKind,
  type ConnectedAdAccount,
  disconnectAdAccount,
} from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { env } from '../lib/env.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { formatDate } from '../lib/format.js';
import { META_APPROVED } from '../lib/meta-approval.js';
import { adAccountsQuery, brandChannelsQuery, brandsQuery, meQuery } from '../lib/queries.js';
import { refreshQueries } from '../lib/refresh.js';

/**
 * Channels index — `/brand/$slug/channels`. Lists every channel mag
 * is operating (or could operate) for this brand. Spec §0b.6.
 *
 * linkedin / tiktok / youtube render as "coming soon" because their
 * connection adapters are not built. instagram and meta ads render the
 * same way for a DIFFERENT reason — see META_APPROVED.
 *
 * Paid sits above organic: google ads is the only channel a founder can
 * actually act on today, and burying it under four channels they cannot
 * use made the page read as mostly-unavailable.
 */

// META_APPROVED lives in lib/meta-approval.ts — the same offer appears on
// the home rail, in onboarding and in settings, so the flag is shared.

const ALL_CHANNEL_KINDS: BrandChannelKind[] = ['instagram', 'linkedin', 'tiktok', 'youtube'];

export const Route = createFileRoute('/brand/$slug/channels/')({
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const brand = brands.find((b) => b.slug === params.slug);
    if (!brand) throw redirect({ to: '/brands' });
    await Promise.all([
      queryClient.ensureQueryData(brandChannelsQuery(brand.id)),
      queryClient.ensureQueryData(adAccountsQuery(brand.id)),
    ]);
    return { brand };
  },
  component: ChannelsIndexPage,
});

function ChannelsIndexPage() {
  const { t } = useLingui();
  const { brand } = Route.useRouteContext();
  useBrandTitle(brand.slug, ['channels']);
  const channelsQ = useQuery(brandChannelsQuery(brand.id));
  const adAccountsQ = useQuery(adAccountsQuery(brand.id));

  return (
    <div className="min-h-dvh bg-[var(--paper)]">
      <TopNav active="channels" />
      <main className="mx-auto max-w-[760px] px-4 py-8 sm:px-6 sm:py-10">
        <header className="mb-6">
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
            {'↘'} {brand.name.toLowerCase()} · <Trans>channels</Trans>
          </p>
          <h1 className="mt-1 font-display text-[28px] font-bold leading-tight tracking-[-0.02em] text-[var(--ink)]">
            <Trans>where i'm running things.</Trans>
          </h1>
          <p className="mt-2 text-[13px] text-[var(--ink)]/65">
            <Trans>
              organic channels are the perpetual presence i operate; paid platforms are where
              campaigns ship for real. both wire up here.
            </Trans>
          </p>
        </header>

        {/* Paid first. Google ads is the only channel a founder can act on
            today, and it used to sit below four they cannot — so the page
            opened on unavailability and buried the one working thing. */}
        <SectionHeader
          eyebrow={t`paid`}
          title={t`ad platforms`}
          blurb={t`connect once. mag launches search + paid social campaigns against these accounts.`}
        />
        {adAccountsQ.isLoading ? (
          <Loading />
        ) : (
          <PaidPlatformGrid brand={brand} accounts={adAccountsQ.data?.accounts ?? []} />
        )}

        <SectionHeader
          eyebrow={t`organic`}
          title={t`always-on channels`}
          blurb={t`where i post on cadence between campaigns.`}
          className="mt-10"
        />
        {channelsQ.isLoading ? (
          <Loading />
        ) : (
          <ChannelGrid brand={brand} channels={channelsQ.data ?? []} />
        )}
      </main>
    </div>
  );
}

function SectionHeader({
  eyebrow,
  title,
  blurb,
  className,
}: {
  eyebrow: string;
  title: string;
  blurb: string;
  className?: string;
}) {
  return (
    <header className={`mb-3 ${className ?? ''}`}>
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
        {'↘'} {eyebrow}
      </p>
      <h2 className="mt-0.5 font-display text-[18px] font-bold tracking-[-0.01em] text-[var(--ink)]">
        {title}
      </h2>
      <p className="mt-1 text-[12.5px] text-[var(--ink)]/60">{blurb}</p>
    </header>
  );
}

function Loading() {
  return (
    <p className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-6 text-center font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/45 shadow-[4px_4px_0_var(--spell)]">
      <Trans>reading channels…</Trans>
    </p>
  );
}

function ChannelGrid({ brand, channels }: { brand: Brand; channels: BrandChannelDTO[] }) {
  const byKind = new Map(channels.map((c) => [c.kind, c]));
  return (
    <ul className="flex flex-col gap-3">
      {ALL_CHANNEL_KINDS.map((kind) => {
        const channel = byKind.get(kind) ?? null;
        return <ChannelRow key={kind} kind={kind} channel={channel} brand={brand} />;
      })}
    </ul>
  );
}

function ChannelRow({
  kind,
  channel,
  brand,
}: {
  kind: BrandChannelKind;
  channel: BrandChannelDTO | null;
  brand: Brand;
}) {
  const { t } = useLingui();
  const supported = kind === 'instagram' && META_APPROVED;
  if (!supported) {
    return (
      <li>
        <div className="rounded-2xl border-[1.5px] border-dashed border-[var(--ink)]/20 bg-white/40 p-5 sm:p-6">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/45">
              ↘ {kind}
            </p>
            <span className="font-mono text-[9.5px] uppercase tracking-[0.08em] text-[var(--ink)]/35">
              <Trans>coming soon</Trans>
            </span>
          </div>
          <h3 className="mt-1 font-display text-[16px] font-bold tracking-[-0.01em] text-[var(--ink)]/45">
            {labelForKind(kind)}
          </h3>
          <p className="mt-1 text-[12.5px] text-[var(--ink)]/45">
            {/* Two different reasons wear the same badge, and saying the
                wrong one is worse than saying nothing: instagram's adapter
                is built and waiting on meta's review, so "once the adapter
                ships" would be a false explanation of a delay the founder
                may well ask us about. */}
            {kind === 'instagram' ? (
              <Trans>
                built and waiting on meta's app review. i'll turn instagram on the day it clears.
              </Trans>
            ) : (
              <Trans>i'll be able to operate {kind} once the connection adapter ships.</Trans>
            )}
          </p>
        </div>
      </li>
    );
  }
  return (
    <li>
      <Link
        to="/brand/$slug/channels/$kind"
        params={{ slug: brand.slug, kind }}
        className="block rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] transition-all hover:shadow-[5px_5px_0_var(--spell)] sm:p-6"
      >
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
            ↘ {kind}
          </p>
          {channel ? <StatusChip status={channel.status} /> : <NotConnectedChip />}
        </div>
        <h3 className="mt-1 font-display text-[18px] font-bold tracking-[-0.01em] text-[var(--ink)]">
          {labelForKind(kind)}
        </h3>
        <p className="mt-1 text-[12.5px] text-[var(--ink)]/65">
          {channel
            ? describeChannel(t, channel)
            : t`not connected yet. connect to let me start posting.`}
        </p>
      </Link>
    </li>
  );
}

function labelForKind(kind: BrandChannelKind): string {
  switch (kind) {
    case 'instagram':
      return 'instagram';
    case 'linkedin':
      return 'linkedin';
    case 'tiktok':
      return 'tiktok';
    case 'youtube':
      return 'youtube';
  }
}

type T = ReturnType<typeof useLingui>['t'];

function describeChannel(t: T, channel: BrandChannelDTO): string {
  if (channel.status === 'archived') {
    return i18n._(msg`archived. historical posts kept; no new drafting.`);
  }
  if (channel.status === 'pending_connection') {
    return i18n._(msg`connection in flight. once it's done i'll start drafting your plan.`);
  }
  if (channel.pausedUntil) {
    const until = new Date(channel.pausedUntil);
    if (until.getTime() > Date.now()) {
      return i18n._(msg`paused until ${formatDate(until, { month: 'short', day: 'numeric' })}.`);
    }
  }
  if (!channel.operatedByMag) {
    return i18n._(msg`you're handling this one yourself; i'm not drafting here.`);
  }
  return i18n._(msg`active. drafting + publishing on cadence.`);
}

function StatusChip({ status }: { status: BrandChannelDTO['status'] }) {
  const { t } = useLingui();
  const tone =
    status === 'active'
      ? 'bg-emerald-50 text-emerald-900'
      : status === 'archived'
        ? 'bg-zinc-100 text-zinc-700'
        : 'bg-amber-50 text-amber-900';
  // Our own channel-status enum, rendered as the chip's prose — so it
  // gets a translated label rather than the raw key.
  const label =
    status === 'active'
      ? t`active`
      : status === 'archived'
        ? t`archived`
        : status === 'pending_connection'
          ? t`pending`
          : status;
  return (
    <span
      className={`rounded-full px-2 py-0.5 font-mono text-[9.5px] uppercase tracking-[0.08em] ${tone}`}
    >
      {label}
    </span>
  );
}

function NotConnectedChip() {
  return (
    <span className="rounded-full bg-zinc-50 px-2 py-0.5 font-mono text-[9.5px] uppercase tracking-[0.08em] text-zinc-700">
      <Trans>not connected</Trans>
    </span>
  );
}

// ─── Paid platforms ──────────────────────────────────────────────
//
// Mirrors the organic ChannelGrid shape but each row points at an
// ad-platform connection (google ads, meta ads). Mock-mode connects
// (status === 'mock') are surfaced explicitly so the founder isn't
// confused by "looks connected but ads stay in mock". Connect kicks
// off the OAuth flow via the api's start endpoint; returnTo lands the
// founder back on this page so the round-trip feels seamless.

type PaidProvider = 'google' | 'meta';

const PAID_PROVIDERS: PaidProvider[] = ['google', 'meta'];

function PaidPlatformGrid({ brand, accounts }: { brand: Brand; accounts: ConnectedAdAccount[] }) {
  const byProvider = new Map<PaidProvider, ConnectedAdAccount[]>();
  for (const p of PAID_PROVIDERS) byProvider.set(p, []);
  for (const acc of accounts) {
    const bucket = byProvider.get(acc.provider as PaidProvider);
    if (bucket) bucket.push(acc);
  }
  return (
    <ul className="flex flex-col gap-3">
      {PAID_PROVIDERS.map((provider) => (
        <PaidPlatformRow
          key={provider}
          provider={provider}
          accounts={byProvider.get(provider) ?? []}
          brand={brand}
        />
      ))}
    </ul>
  );
}

function PaidPlatformRow({
  provider,
  accounts,
  brand,
}: {
  provider: PaidProvider;
  accounts: ConnectedAdAccount[];
  brand: Brand;
}) {
  const { t } = useLingui();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<ConnectedAdAccount | null>(null);
  const [working, setWorking] = useState<string | null>(null);

  // Active = anything currently usable for launches. Mock counts —
  // mock-mode means the api will simulate a launch without hitting
  // the platform; useful for local dev, surfaced honestly.
  const liveAccounts = accounts.filter((a) => a.status !== 'disconnected');
  const isConnected = liveAccounts.length > 0;
  const platformLabel = labelForPaidProvider(provider);
  // Round-trip the founder back here after OAuth. The api's start
  // endpoint allowlists same-origin paths; the channels index is a
  // safe destination so the connect flow feels like one page transition.
  const returnTo = `/brand/${brand.slug}/channels`;
  const connectUrl = `${env.API_URL}/api/brands/${brand.id}/${provider}/connect/start?returnTo=${encodeURIComponent(returnTo)}`;

  const performDisconnect = async (acc: ConnectedAdAccount) => {
    setWorking(acc.id);
    try {
      await disconnectAdAccount(acc.id);
      magToast.success(
        t`disconnected ${platformLabel}.`,
        t`tokens cleared. mag won't touch this account again.`,
      );
      setConfirming(null);
      await refreshQueries(queryClient, ['ad-accounts']);
      await router.invalidate();
    } catch (err) {
      magToast.error("couldn't disconnect.", founderErrorCopy(err));
    } finally {
      setWorking(null);
    }
  };

  // Meta ads is gated on the same app review instagram is. Offering
  // "connect meta ads" would hand the founder an OAuth round-trip that
  // dead-ends at meta's consent screen — the dashed card says so instead.
  // An ALREADY-connected meta account keeps its row: severing a working
  // connection because a flag flipped would be a worse surprise than the
  // one this prevents.
  if (provider === 'meta' && !META_APPROVED && !isConnected) {
    return (
      <li>
        <div className="rounded-2xl border-[1.5px] border-dashed border-[var(--ink)]/20 bg-white/40 p-5 sm:p-6">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/45">
              ↘ {platformLabel}
            </p>
            <span className="font-mono text-[9.5px] uppercase tracking-[0.08em] text-[var(--ink)]/35">
              <Trans>coming soon</Trans>
            </span>
          </div>
          <h3 className="mt-1 font-display text-[16px] font-bold tracking-[-0.01em] text-[var(--ink)]/45">
            {platformLabel}
          </h3>
          <p className="mt-1 text-[12.5px] text-[var(--ink)]/45">
            <Trans>
              built and waiting on meta's app review. until it clears, google ads is where campaigns
              ship for real.
            </Trans>
          </p>
        </div>
      </li>
    );
  }

  return (
    <li>
      <article className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
            ↘ {platformLabel}
          </p>
          {isConnected ? (
            <PaidStatusChip status={liveAccounts[0]?.status ?? 'connected'} />
          ) : (
            <NotConnectedChip />
          )}
        </div>
        <h3 className="mt-1 font-display text-[18px] font-bold tracking-[-0.01em] text-[var(--ink)]">
          {platformLabel}
        </h3>
        <p className="mt-1 text-[12.5px] text-[var(--ink)]/65">
          {describePaidProvider(t, provider, isConnected)}
        </p>

        {isConnected ? (
          <ul className="mt-4 flex flex-col gap-2">
            {liveAccounts.map((a) => (
              <li
                key={a.id}
                className="flex items-center justify-between gap-3 rounded-xl border border-[var(--ink)]/15 bg-[var(--spell-soft)]/40 p-3 sm:p-4"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-serif text-[14.5px] leading-tight text-[var(--ink)]">
                    <Trans>
                      connected as <b>{a.accountLabel}</b>
                    </Trans>
                  </p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
                    {'↘'} <Trans>account id</Trans> · {a.providerCustomerId}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setConfirming(a)}
                  disabled={working !== null}
                  className="btn btn-ghost text-[11px] disabled:opacity-60"
                >
                  {working === a.id ? t`disconnecting…` : t`disconnect`}
                  <span className="btn-arrow">→</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-4 flex items-center justify-end">
            <a href={connectUrl} className="btn">
              <Trans>connect {platformLabel}</Trans>
              <span className="btn-arrow">→</span>
            </a>
          </div>
        )}
      </article>

      <ConfirmDialog
        open={confirming !== null}
        title={confirming ? t`disconnect ${confirming.accountLabel}?` : ''}
        body={
          <Trans>
            mag will lose access immediately. campaigns + analyses stay on this brand — only the
            live link to {platformLabel} goes away.
          </Trans>
        }
        confirmLabel={t`disconnect`}
        busyLabel={t`disconnecting…`}
        destructive
        busy={confirming !== null && working === confirming.id}
        onConfirm={() => (confirming ? performDisconnect(confirming) : Promise.resolve())}
        onCancel={() => setConfirming(null)}
      />
    </li>
  );
}

function labelForPaidProvider(provider: PaidProvider): string {
  switch (provider) {
    case 'google':
      return 'google ads';
    case 'meta':
      return 'meta ads';
  }
}

function describePaidProvider(t: T, provider: PaidProvider, isConnected: boolean): string {
  if (isConnected) {
    return provider === 'google'
      ? i18n._(msg`search campaigns launch straight to this account.`)
      : i18n._(msg`paid instagram + facebook campaigns launch straight to this account.`);
  }
  return provider === 'google'
    ? i18n._(msg`connect to let me launch search campaigns. until then launches stay in mock mode.`)
    : i18n._(
        msg`connect to let me launch paid instagram + facebook ads. until then launches stay in mock mode.`,
      );
}

function PaidStatusChip({ status }: { status: ConnectedAdAccount['status'] }) {
  const { t } = useLingui();
  const tone =
    status === 'connected'
      ? 'bg-emerald-50 text-emerald-900'
      : status === 'mock'
        ? 'bg-amber-50 text-amber-900'
        : 'bg-zinc-100 text-zinc-700';
  // Same call as StatusChip above: our own enum, rendered as prose.
  const label =
    status === 'connected' ? t`connected` : status === 'mock' ? t`mock` : t`disconnected`;
  return (
    <span
      className={`rounded-full px-2 py-0.5 font-mono text-[9.5px] uppercase tracking-[0.08em] ${tone}`}
    >
      {label}
    </span>
  );
}
